import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { LinkIcon } from "lucide-react";
import { PaymentTemplate } from "@/components/PaymentTemplate";
import { api, apiError } from "@/lib/api";
import { payT } from "@/lib/i18n";

export const InvalidLink = () => (
  <div className="admin-shell flex min-h-screen flex-col items-center justify-center bg-[#F5F6F8] px-6 text-center" data-testid="pay-not-found">
    <LinkIcon size={36} className="text-slate-300" />
    <h1 className="mt-4 font-heading text-2xl font-semibold" data-testid="invalid-link-title">Enlace no válido</h1>
    <p className="mt-2 max-w-sm text-sm text-slate-500">Este enlace de pago ya no está disponible. Si necesitas ayuda, contacta al hotel.</p>
    <p className="mt-1 max-w-sm text-xs text-slate-400">Invalid link · This payment link is no longer available.</p>
  </div>
);

function payStateFor(info, returning, loading, error) {
  const c = info.charge;
  const t = payT(info.hotel?.language);
  if (c.status === "pagado") return { disabled: true, tone: "ok", notice: t.paid, label: t.paidLabel };
  if (c.status === "cancelado") return { disabled: true, notice: t.cancelled };
  if (c.status === "vencido") return { disabled: true, notice: t.expired };
  if (!info.processor_connected) return { disabled: true, pending: true, notice: t.notConnected };
  if (returning) return { disabled: true, notice: t.confirming };
  return { disabled: loading, loading, notice: error && (t.startError || error) };
}

export default function PublicPay() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const returning = sp.get("resultado") === "retorno";
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [embed, setEmbed] = useState(null);
  const [redirectUrl, setRedirectUrl] = useState(null);
  const [retry, setRetry] = useState(0);

  const navigate = useNavigate();
  const load = useCallback(() => api.get(`/pay/${token}`, { params: returning ? { check: 1 } : {} }).then((r) => setInfo(r.data))
    .catch(() => navigate("/enlace-no-valido", { replace: true })), [token, returning, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const c = info?.charge;
    if (!info || !info.processor_connected || returning || c.status !== "pendiente" || embed || redirectUrl) return;
    api.post(`/pay/${token}/checkout`).then(({ data }) => {
      setError(null);
      if (data.embedded) setEmbed({ ...data, returnUrl: `${window.location.origin}/pago/${token}?resultado=retorno` });
      else setRedirectUrl(data.url);
    }).catch((e) => setError(apiError(e).message));
  }, [info, returning, token, embed, redirectUrl, retry]);
  useEffect(() => {
    if (info?.charge?.status !== "pendiente") return undefined;
    const t = setInterval(load, returning ? 5000 : 15000);
    return () => { clearInterval(t); };
  }, [returning, info, load]);

  const pay = () => {
    if (redirectUrl) { window.location.href = redirectUrl; return; }
    setError(null); setRetry((n) => n + 1);
  };

  if (!info) return <div className="min-h-screen bg-[#F5F6F8]" data-testid="pay-loading" />;
  return <PaymentTemplate hotel={info.hotel} data={info.charge} payState={payStateFor(info, returning, !redirectUrl && !error && !embed, error)} onPay={pay} embed={embed} />;
}
